import {
  CloneRoleInput,
  UpdateRoleInput,
  CreateCustomerRoleInput,
} from "../../../hooks/useRoleMutations";
import {
  CreateEhsRoleInput,
  PermissionsFilterInput,
  RolesFilterInput,
  UpdateEhsRoleInput,
} from "../../routeComponents/EhsConfiguration/EhsRoleTypes";
import {
  GetUserRoleLocationCountFilter,
  GetUsersAssignedToRoleFilter,
} from "../../routeComponents/RoleManagement/Utils";
import { PaginationQueryObject, QueryConfig } from "./Queries";

export const customerRolesListQuery = (
  customerId: string,
  filter: any,
  pagination: PaginationQueryObject
): QueryConfig => {
  const query = `query getCustomerRoles($customerId: ID, $filter: RoleFilter, $pagination: PaginationInput) {
        customer(id: $customerId) {
            roles(filter: $filter, paginationInput: $pagination) {
                items { id, name, description },
                pageInfo { pageSize, pageNumber, totalElements, totalPages, hasNext, hasPrevious }
            }
        }
    }`;
  return {
    query: query,
    variables: {
      customerId: customerId,
      filter: filter,
      pagination: pagination,
    },
  };
};

export const ehsRolesListQuery = (
  paginationInput: PaginationQueryObject,
  filterInput: RolesFilterInput
): QueryConfig => {
  const query = `query ehsRoles(
    $paginationInput: PaginationInput!
    $filterInput: EhsRolesFilterInput
  ) {
    ehsRoles(
      paginationInput: $paginationInput
      filterInput: $filterInput
    ) {
      items {
        id
        name
        description
        activePermissionMappings {
          permission {
            id
            name
          }
          assignedQualifiers {
            id
            name
          }
        }
      }
      pageInfo {
        pageSize
        pageNumber
        totalElements
        totalPages
        hasNext
        hasPrevious
      }
    }
  }`;

  return {
    query: query,
    variables: {
      paginationInput,
      filterInput,
    },
  };
};

export const getEhsCapabilitiesListQuery = (input: {
  customerId: string;
}): QueryConfig => {
  const query = `query ehsCapabilities($input: EhsCapabilityInput!) {
    ehsCapabilities(input: $input) {
      items {
        id
        name
      }
    }
  }`;

  return {
    query: query,
    variables: {
      input,
    },
  };
};

export const getEhsPermissionsListQuery = (
  paginationInput: PaginationQueryObject,
  filterInput: PermissionsFilterInput
): QueryConfig => {
  const query = `query ehsPermissions(
    $paginationInput: PaginationInput!
    $filterInput: EhsPermissionFilterInput!
  ) {
    ehsPermissions(
      paginationInput: $paginationInput
      filterInput: $filterInput
    ) {
      items {
        id
        name
        capability
        availableQualifiers {
          id
          name
        }
      }
      pageInfo { pageSize, pageNumber, totalElements, totalPages, hasNext, hasPrevious }
    }
  }`;
  return {
    query: query,
    variables: {
      paginationInput,
      filterInput,
    },
  };
};

export const ehsRoleQuery = (customerId: string, id: string): QueryConfig => {
  const query = `query ehsRole($input: EhsRoleInput) {
    ehsRole(input: $input) {
      id
      name
      description
      activePermissionMappings {
        permission {
          id
          name
        }
        assignedQualifiers {
          id
          name
        }
      }
    }
  }`;

  return {
    query: query,
    variables: {
      input: {
        customerId,
        id,
      },
    },
  };
};

export const createEhsRoleMutation = (
  input: CreateEhsRoleInput
): QueryConfig => {
  const query = `mutation createEhsRole($input: CreateEhsRoleInput!) {
    createEhsRole(input: $input) {
      description
      id
      name
    }
  }`;
  return {
    query: query,
    variables: {
      input,
    },
  };
};

export const updateEhsRoleMutation = (
  input: UpdateEhsRoleInput
): QueryConfig => {
  const query = `mutation updateEhsRole($input: UpdateEhsRoleInput!) {
    updateEhsRole(input: $input) {
      description
      id
      name
    }
  }`;
  return {
    query: query,
    variables: {
      input,
    },
  };
};

export const userRoleLocationCountQuery = (
  paginationInput: PaginationQueryObject,
  filter: GetUserRoleLocationCountFilter
): QueryConfig => {
  const query = `query getUserRoleLocationCount(
    $filter: UserRoleCountFilter
    $paginationInput: PaginationInput
  ) {
    userRoleLocationCount(filter: $filter, paginationInput: $paginationInput) {
      pageInfo {
        pageNumber
        pageSize
        totalElements
        hasNext
        hasPrevious
      }
      items {
        roleID
        roleName
        roleDescription
        locationID
        locationName
        count
      }
    }
  }`;

  return {
    query: query,
    variables: {
      paginationInput,
      filter,
    },
  };
};

export const userRoleLocationDetailsQuery = (
  paginationInput: PaginationQueryObject,
  filter: GetUsersAssignedToRoleFilter
): QueryConfig => {
  const query = `query getUserRoleLocationDetails(
    $filter: UserRoleLocationFilter
    $paginationInput: PaginationInput
  ) {
    userRoleLocationDetails(filter: $filter, paginationInput: $paginationInput) {
      pageInfo {
        totalElements
        hasNext
        hasPrevious
        pageNumber
        pageSize
      }
      items {
        roleID
        roleName
        email
        locationName
        displayName
        userStatus
        userID
        isExplicitlyAssigned
      }
    }
  }`;

  return {
    query: query,
    variables: {
      paginationInput,
      filter,
    },
  };
};

export const customerRoles = (
  id: string,
  filter: any,
  paginationInput?: PaginationQueryObject
): QueryConfig => {
  const query = `query customerRoles(
    $id: ID
    $filter: RoleFilter
    $paginationInput: PaginationInput
  ) {
    customer(id: $id) {
      roles(filter: $filter, paginationInput: $paginationInput) {
        pageInfo {
          pageNumber
          pageSize
          totalPages
          totalElements
          hasNext
          hasPrevious
        }
        items {
          id
          name
          description
          applicationRelationship
          editableByUser
        }
      }
    }
  }`;

  return {
    query: query,
    variables: {
      id,
      filter,
      paginationInput,
    },
  };
};

export const rolePoliciesQuery = (
  id: string,
  filter: any,
  paginationInput: PaginationQueryObject
): QueryConfig => {
  const query = `query rolePolicies(
    $id: ID
    $filter: RoleFilter
    $paginationInput: PaginationInput
  ) {
    customer(id: $id) {
      roles(filter: $filter) {
        items {
          id
          name
          description
          applicationRelationship
          policies(paginationInput: $paginationInput){
            items{
              id
              name
              description
              applicationRelationship
              externalAccessAllowed
            }
            pageInfo{
              totalElements
            }
          }
        }
      }
    }
  }`;

  return {
    query: query,
    variables: {
      id,
      filter,
      paginationInput,
    },
  };
};

export const updateRole = (input: UpdateRoleInput): QueryConfig => {
  const query = `mutation updateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) {
      id
    }
  }`;

  return {
    query: query,
    variables: {
      input,
    },
  };
};

export const cloneRoleQuery = (input: CloneRoleInput): QueryConfig => {
  const query = `mutation cloneRole($input: CloneRoleInput!) {
    cloneRole(input: $input) {
      id
    }
  }`;

  return {
    query: query,
    variables: {
      input,
    },
  };
};

export const createCustomerRole = (
  input: CreateCustomerRoleInput
): QueryConfig => {
  const query = `mutation createCustomerRole($input: CreateCustomerRoleInput!) {
    createCustomerRole(input: $input) {
      role {
        id
      }
    }
  }`;

  return {
    query: query,
    variables: {
      input,
    },
  };
};
